import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  connectedCallback() {
    super.connectedCallback();
    // A5 (31 Oct 2026): Enter or Go in the quantity field updates the line
    // and never submits the cart form, whose first submit button is Check
    // out. Blurring runs the same setQuantity path as leaving the field.
    this.onQuantityKeydown = (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      if (event.target instanceof HTMLElement) event.target.blur();
    };
    this.refs.quantityInput?.addEventListener('keydown', this.onQuantityKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.onQuantityKeydown) this.refs.quantityInput?.removeEventListener('keydown', this.onQuantityKeydown);
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
